// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isApiError from "@insite/client-framework/Common/isApiError";
import { ApiParameter, patch, post, request, ServiceResult } from "@insite/client-framework/Services/ApiService";
import { QuoteLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";

const quoteUrl = "/api/custom/quote";

export interface AddNewGroup extends ApiParameter {
    cartId: string;
    newBlankGroupName: string;
}

export interface RenameGroupName extends ApiParameter {
    cartId: string;
    originalGroupName: string;
    newGroupName: string;
}

export interface DeleteGroup extends ApiParameter {
    cartId: string;
    originalGroupName: string;
}

export interface CustomQuoteLineModel {
    lineId: string;
    sortOrder?: number;
    qtyOrdered?: number;
    notes?: string;
    parentId?: string;
    originalGroupName?: string;
}

export interface MoveCopyOrDeleteItem extends ApiParameter {
    cartId: string;
    originalGroupName: string;
    newGroupName: string;
    linesModel: CustomQuoteLineModel[];
}

export interface UpdateLineInfo extends ApiParameter {
    cartId: string;
    originalGroupName: string;
    linesModel: CustomQuoteLineModel[];
}

export interface DeleteRelatedLine extends ApiParameter {
    cartId: string;
    originalGroupName: string;
    linesModel: { lineId: string; parentId: string }[];
}

export interface CreateCustomQuote extends ApiParameter {
    quoteId: string;
}

export interface CreateProjectList extends ApiParameter {
    quoteId: string;
}

export async function addNewGroup(parameter: AddNewGroup) {
    try {
        const cart = await post<any>(`${quoteUrl}/AddNewBlankGroup`, parameter);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: error.errorMessage,
        };
    }
}

export async function renameGroupName(paramter: RenameGroupName) {
    try {
        const newCart = await patch<any>(`${quoteUrl}/RenameGroup`, paramter);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error,
        };
    }
}

export async function moveLines(parameter: MoveCopyOrDeleteItem) {
    try {
        const cart = await patch<any>(`${quoteUrl}/UpdateGroup`, parameter);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: error.errorMessage,
        };
    }
}

export async function copyLines(parameter: MoveCopyOrDeleteItem) {
    try {
        const cart = await patch<any>(`${quoteUrl}/CopyToGroup`, parameter);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: error.errorMessage,
        };
    }
}

export async function deleteLines(parameter: MoveCopyOrDeleteItem) {
    try {
        const cart = await patch<any>(`${quoteUrl}/UpdateGroup`, parameter);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: error.errorMessage,
        };
    }
}

export async function deleteGroup(parameter: DeleteGroup) {
    try {
        const cart = await patch<any>(`${quoteUrl}/DeleteGroup`, parameter);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: error.errorMessage,
        };
    }
}

export async function updateLineInfo(paramter: UpdateLineInfo) {
    try {
        const cart = await patch<any>(`${quoteUrl}/UpdateGroupLineInfo`, paramter);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error: error.errorMessage,
        };
    }
}

export async function deleteRelatedLine(parameter: DeleteRelatedLine) {
    try {
        const cart = await patch<any>(`${quoteUrl}/DeleteRelatedItem`, parameter);
        return {
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            error,
        };
    }
}

const ootbQuotesUrl = "/api/v1/quotes";

export interface UpdateQuoteLineApiParameter extends ApiParameter {
    quoteId: string;
    quoteLineId: string;
    quoteLine: QuoteLineModel;
    updatePriceBreakInfo?: boolean;
    isFixedQty?: boolean;
    exactQty?: string;
    minimumQty?: string;
    originalGroupName?: string;
}

export async function updateQuoteLine(parameter: UpdateQuoteLineApiParameter): Promise<ServiceResult<QuoteLineModel>> {
    let params = {} as any;

    // customer view updating qty
    if (parameter.originalGroupName) {
        params = {
            ...parameter.quoteLine,
            properties: {
                ...parameter.quoteLine.properties,
                originalGroupName: parameter.originalGroupName,
            },
        };
        // sales rep view updating price breaks
    } else {
        params.id = parameter.quoteLine.id;
        params.pricingRfq = parameter.quoteLine.pricingRfq;
    }

    if (parameter.updatePriceBreakInfo) {
        params.properties = {} as any;
        params.properties.updatePriceBreakInfo = true;
        params.properties.isFixedQty = parameter.isFixedQty;
        params.properties.exactQty = parameter.exactQty;
        params.properties.minimumQty = parameter.minimumQty;
    }

    try {
        const quoteLine = await patch<any>(
            `${ootbQuotesUrl}/${parameter.quoteId}/quotelines/${parameter.quoteLineId}`,
            {
                ...params,
            },
        );
        return {
            successful: true,
            result: quoteLine,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export interface UpdateQuoteLinesListApiParameter extends ApiParameter {
    quoteId: string;
    quoteLines: QuoteLineModel[];
}

export async function updateQuoteLinesList(
    parameter: UpdateQuoteLinesListApiParameter,
): Promise<ServiceResult<string>> {
    try {
        const quoteLine = await patch<any>(`api/custom/quote/${parameter.quoteId}/quoteLines`, parameter.quoteLines);
        return {
            successful: true,
            result: "",
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export interface UpdateQuoteApiParameter extends ApiParameter {
    quoteId: string;
    status?: string;
    note?: string;
    userId?: string;
    expirationDate?: Date;
    calculationMethod?: string;
    percent?: number;
    isJobQuote?: boolean;
    jobName?: string;
    orderQuantities?: QuoteLineModel[];
    isQuoteFreight?: boolean;
    freightCost?: string;
    followUpDate?: Date;
    rejectReason?: string;
}

export async function updateQuote(parameter: UpdateQuoteApiParameter): Promise<ServiceResult<QuoteModel>> {
    try {
        const quote = await patch<QuoteModel>(
            `${ootbQuotesUrl}/${parameter.quoteId}?${
                parameter.followUpDate ? `followUpDate=${parameter.followUpDate}` : ""
            }${parameter.isQuoteFreight ? `&isQuoteFreight=${parameter.isQuoteFreight}` : ""}${
                parameter.isQuoteFreight && parameter.freightCost ? `&freightCost=${parameter.freightCost}` : ""
            }${
                parameter.rejectReason ? `&rejectReason=${parameter.rejectReason}` : ""
            }`,
            parameter,
        );
        cleanQuote(quote);
        return {
            successful: true,
            result: quote,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

function cleanQuote(quote: QuoteModel) {
    quote.orderDate = quote.orderDate ? new Date(quote.orderDate) : null;
    quote.expirationDate = quote.expirationDate ? new Date(quote.expirationDate) : null;
    quote.cartLines = quote.quoteLineCollection;
    quote.messageCollection?.forEach(message => {
        if (message.createdDate) {
            message.createdDate = new Date(message.createdDate);
        }
    });
}

const uploadLogoUrl = "/api/custom/quote/UploadLogo";

export interface UploadLogoParams {
    formData: any;
}

export async function uploadLogo(parameter: UploadLogoParams): Promise<ServiceResult<any>> {
    try {
        const result = await request<any>(
            `${uploadLogoUrl}`,
            "POST",
            {},
            parameter ? (parameter.formData as any) : undefined,
        );
        return {
            result,
            successful: true,
        };
    } catch (error) {
        if ("status" in error && error?.status === 400 && error?.errorJson && error?.errorJson?.message) {
            return {
                successful: false,
                errorMessage: error?.errorJson?.message,
            };
        }
        throw error;
    }
}

export async function convertToCustomQuote(params: CreateCustomQuote) {
    try {
        const quoteId = await post<any>(`${quoteUrl}/${params.quoteId}/ConvertToCustomQuote`);
        return {
            successful: true,
            customQuoteId: quoteId.customQuoteId,
        };
    } catch (error) {
        return {
            successful: false,
            error: error?.errorJson?.message,
        };
    }
}

export async function convertToProjectList(params: CreateCustomQuote) {
    try {
        const quoteId = await post<any>(`${quoteUrl}/${params.quoteId}/ConvertToProjectList`);
        return {
            successful: true,
            wishListId: quoteId.wishListId,
        };
    } catch (error) {
        return {
            successful: false,
            error: error?.errorJson?.message,
        };
    }
}
